import { useTranslation } from "react-i18next";
import { CheckCircle2, MessageSquareText, PencilLine } from "lucide-react";
import { formatDateTime } from "@/lib/dateFormatters";
import type { VisitReport } from "@/lib/planner/visitReport";

/**
 * Quién y cuándo, y el comentario de la empresa, debajo del informe de una visita.
 *
 * La ficha solo decía la fecha PROGRAMADA: una visita del 27-09 hecha el 25-09
 * salía como del 27-09 y no decía quién la hizo (T07-06). Y el comentario de la
 * empresa va aparte del informe del perito (C3): se enseña con su autor y su
 * fecha, sin mezclarse con lo que escribió él.
 */
export function VisitReportMeta({
  report,
  completedAt,
  status,
  names,
  assigneeId,
  compact = false,
}: {
  report: VisitReport | null | undefined;
  completedAt?: string | null;
  status?: string | null;
  names: Record<string, string>;
  assigneeId?: string | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const safeDate = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : formatDateTime(d);
  };
  const doneAt = safeDate(report?.completed_at || completedAt);
  const doneById = report?.completed_by || (status === "completed" ? assigneeId : null);
  const doneBy = doneById ? names[doneById] : undefined;
  const editedAt = safeDate(report?.edited_at);
  const editedBy = report?.edited_by ? names[report.edited_by] : undefined;
  const comment = report?.company_comment;
  const commentAt = safeDate(comment?.at);
  const commentBy = comment?.by ? names[comment.by] : undefined;

  const line = compact ? "text-[12px]" : "text-xs";
  if (status !== "completed" && !editedAt && !comment?.text) return null;

  return (
    <div className="space-y-2" data-testid="visit-report-meta">
      {status === "completed" && doneAt && (
        <p className={`${line} text-muted-foreground flex items-center gap-1.5`}>
          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
          {doneBy
            ? t("perito.seguimiento.doneOnBy", { date: doneAt, name: doneBy })
            : t("perito.seguimiento.doneOn", { date: doneAt })}
        </p>
      )}
      {editedAt && (
        <p className={`${line} text-muted-foreground flex items-center gap-1.5`}>
          <PencilLine className="w-3.5 h-3.5 shrink-0" />
          {editedBy
            ? t("perito.seguimiento.editedOnBy", { date: editedAt, name: editedBy })
            : t("perito.seguimiento.editedOn", { date: editedAt })}
        </p>
      )}
      {comment?.text && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3" data-testid="visit-company-comment">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-800 flex items-center gap-1.5">
            <MessageSquareText className="w-3.5 h-3.5" />
            {t("perito.seguimiento.companyComment")}
          </p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{comment.text}</p>
          {(commentBy || commentAt) && (
            <p className="text-[11px] text-muted-foreground mt-1">
              {[commentBy, commentAt].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
